// Consolidated mapping utilities for Call Dashboard components
import React from "react";

// Reverse mapping: brand name text -> brand ID (for when NetSuite returns text instead of ID)
export const BRAND_NAME_TO_ID = {
    "Academic Institute of Excellence (AIE)": "1",
    "AIE": "1",
    "Academic Institute of Excellence (AIE)": "2",
    "AIE": "2",
    "Emendy Multimedia Institute": "3",
    "Emendy": "3",
    "Greenside Design Center (GDC)": "4",
    "GDC": "4",
    "Greenside Design Center": "4",
    "LearnFast": "5",
    "The CAD Corporation": "6",
    "CAD Corporation": "6",
    "CADCO": "6"
};

// Helper function to normalize brand names from API to display names
export const normalizeBrandName = (brandName) => {
    if (!brandName) return null;
    const nameLower = String(brandName).toLowerCase().trim();
    
    // Normalize "AIE Group" and variations to proper display name
    if (nameLower.includes("aie group") || nameLower === "aie") {
        return "Academic Institute of Excellence (AIE)";
    }
    if (nameLower.includes("emendy") || nameLower.includes("emi")) {
        return "Emendy Multimedia Institute";
    }
    if (nameLower.includes("greenside") || nameLower.includes("gdc")) {
        return "Greenside Design Center (GDC)";
    }
    if (nameLower.includes("learnfast") || nameLower.includes("lf")) {
        return "LearnFast";
    }
    if (nameLower.includes("cad") || nameLower.includes("cadco")) {
        return "The CAD Corporation";
    }
    
    return null; // No normalization needed
};

// Helper to normalize subsidiary to an ID that RenderBrandTemplate understands
export const normalizeBrandToId = (subsidiary, brandMapping = {}) => {
    if (!subsidiary) return null;
    const str = String(subsidiary).trim();
    
    // If it's already a numeric ID, return as-is
    if (/^\d+$/.test(str)) return str;
    
    // Handle concatenated values (e.g., "AIE Group : AIE : EMD") - take the first part
    const firstPart = str.split(':')[0].trim();
    
    // First, try to find in API brandMapping by checking if any brand name contains the first part
    if (brandMapping && Object.keys(brandMapping).length > 0) {
        // Check if firstPart matches any brand ID in the mapping
        for (const [id, name] of Object.entries(brandMapping)) {
            if (name && name.toLowerCase().includes(firstPart.toLowerCase())) {
                return id;
            }
        }
        // Also check if firstPart is a brand ID
        if (brandMapping[firstPart]) {
            return firstPart;
        }
    }
    
    // Try to map from static name mapping
    if (BRAND_NAME_TO_ID[firstPart]) {
        return BRAND_NAME_TO_ID[firstPart];
    }
    
    // Try the full string as well
    if (BRAND_NAME_TO_ID[str]) {
        return BRAND_NAME_TO_ID[str];
    }
    
    return null;
};

// Custom brand render function using brandMapping
export const renderBrand = (brandId, brandMapping = {}, options = {}) => {
    const { returnObject = false } = options;
    
    if (!brandId) {
        const emptyResult = { html: null, text: "N/A" };
        if (returnObject) return emptyResult;
        return <span className="cat cat-gray" title="N/A">N/A</span>;
    }

    const brandIdStr = String(brandId).trim();
    let brandName = brandMapping[brandIdStr];
    
    // Normalize brand name from API if needed (e.g., "AIE Group" -> "Academic Institute of Excellence (AIE)")
    if (brandName) {
        const normalized = normalizeBrandName(brandName);
        if (normalized) {
            brandName = normalized;
        }
    } else {
        // If brandId itself is a text name (like "AIE Group"), try to normalize it directly
        const normalized = normalizeBrandName(brandIdStr);
        if (normalized) {
            brandName = normalized;
        }
    }
    
    // If not found in mapping, try static mappings
    if (!brandName) {
        switch (brandIdStr) {
            case "1":
            case "2":
            case "9":
                brandName = "Academic Institute of Excellence (AIE)";
                break;
            case "3":
                brandName = "Emendy Multimedia Institute";
                break;
            case "4":
                brandName = "Greenside Design Center (GDC)";
                break;
            case "5":
                brandName = "LearnFast";
                break;
            case "6":
                brandName = "The CAD Corporation";
                break;
            default:
                // Handle concatenated values (e.g., "AIE Group : AIE : EMD") - take the first part
                const firstPart = brandIdStr.split(':')[0].trim();
                // Try to find in API brandMapping by checking if any brand name contains the first part
                if (brandMapping && Object.keys(brandMapping).length > 0) {
                    for (const [id, name] of Object.entries(brandMapping)) {
                        if (name && name.toLowerCase().includes(firstPart.toLowerCase())) {
                            brandName = name;
                            // Normalize the found name
                            const normalized = normalizeBrandName(brandName);
                            if (normalized) {
                                brandName = normalized;
                            }
                            break;
                        }
                    }
                }
                // Try static name mapping
                if (!brandName && BRAND_NAME_TO_ID[firstPart]) {
                    const mappedId = BRAND_NAME_TO_ID[firstPart];
                    brandName = brandMapping[mappedId];
                    // Normalize if found in mapping
                    if (brandName) {
                        const normalized = normalizeBrandName(brandName);
                        if (normalized) {
                            brandName = normalized;
                        }
                    }
                    // Fallback to static mapping
                    if (!brandName) {
                        switch (mappedId) {
                            case "2": brandName = "Academic Institute of Excellence (AIE)"; break;
                            case "3": brandName = "Emendy Multimedia Institute"; break;
                            case "4": brandName = "Greenside Design Center (GDC)"; break;
                            case "5": brandName = "LearnFast"; break;
                            case "6": brandName = "The CAD Corporation"; break;
                            default: brandName = firstPart; break;
                        }
                    }
                }
                if (!brandName) {
                    // Try to normalize the firstPart itself
                    const normalized = normalizeBrandName(firstPart);
                    brandName = normalized || firstPart || "N/A";
                }
        }
    }

    // Determine color based on brand name
    let colorClass = "cat-gray";
    const nameLower = brandName.toLowerCase();
    if (nameLower.includes("academic institute") || nameLower.includes("aie")) {
        colorClass = "cat-blue";
    } else if (nameLower.includes("emendy")) {
        colorClass = "cat-red";
    } else if (nameLower.includes("greenside") || nameLower.includes("gdc")) {
        colorClass = "cat-green";
    } else if (nameLower.includes("learnfast")) {
        colorClass = "cat-orange";
    } else if (nameLower.includes("cad")) {
        colorClass = "cat-brown";
    }

    const htmlElement = (
        <span className={`cat ${colorClass}`} style={returnObject ? { fontSize: 14 } : {}} title={brandName}>
            <i className="fas fa-circle mr-1"></i> {brandName}
        </span>
    );

    if (returnObject) {
        return {
            html: htmlElement,
            text: brandName
        };
    }
    
    return htmlElement;
};

// Custom campus render function using campusMapping
export const renderCampus = (campusId, campusMapping = {}, options = {}) => {
    const { returnObject = false } = options;
    
    if (!campusId) {
        const emptyResult = { html: null, text: "N/A", color: "cat-gray" };
        if (returnObject) return emptyResult;
        return <span className="cat cat-gray" title="N/A">N/A</span>;
    }

    const campusIdStr = String(campusId).trim();
    let campusName = campusMapping[campusIdStr];
    
    // If not found in mapping, try static mappings
    if (!campusName) {
        switch (campusIdStr) {
            case "1":
                campusName = "Midrand - Block A, Main Campus";
                break;
            case "2":
                campusName = "Waterkant, Cape Town";
                break;
            case "3":
                campusName = "Greenside - Block A, Laubscher Building";
                break;
            case "4":
                campusName = "Online";
                break;
            case "6":
                campusName = "Hatfield, Pretoria";
                break;
            case "8":
                campusName = "Greenside - Block B, Architecture Campus";
                break;
            case "9":
                campusName = "Midrand - Block B, Artisan Campus";
                break;
            case "10":
                campusName = "Midrand - Block C, Artisan Campus";
                break;
            default:
                campusName = campusIdStr;
        }
    }

    // Determine color and icon based on campus name
    let colorClass = "cat-gray";
    let icon = "fa-university";
    const nameLower = campusName.toLowerCase();
    if (nameLower.includes("online")) {
        colorClass = "cat-blue";
        icon = "fa-wifi";
    } else if (nameLower.includes("cape town") || nameLower.includes("waterkant")) {
        colorClass = "cat-greenhex";
    } else if (nameLower.includes("midrand")) {
        colorClass = "cat-skyblue";
    } else if (nameLower.includes("greenside")) {
        colorClass = "cat-brown";
    } else if (nameLower.includes("hatfield") || nameLower.includes("pretoria") || nameLower.includes("centurion")) {
        colorClass = "cat-yellow";
    }

    const result = {
        html: (
            <span className={`cat ${colorClass}`} title={campusName}>
                <i className={`fas ${icon} mr-1`}></i> {campusName}
            </span>
        ),
        text: campusName,
        color: colorClass,
        icon: icon
    };

    if (returnObject) return result;
    return result.html;
};

// Custom relationship render function - handles all possible NetSuite relationship values
export const renderRelationship = (category) => {
    if (!category || category === "-" || category === "") {
        return (
            <span className="ui-element cat cat-gray" title="Contact">
                <i className="fas fa-circle mr-1"></i> Contact
            </span>
        );
    }

    const categoryStr = String(category).trim().toLowerCase();
    const categoryNum = Number(categoryStr);
    
    let relationshipText = "";
    let colorClass = "cat-gray";

    // Handle numeric IDs first (NetSuite internal IDs)
    if (!isNaN(categoryNum) && isFinite(categoryNum)) {
        switch (categoryNum) {
            case 1:
                relationshipText = "Student";
                colorClass = "cat-blue";
                break;
            case 2:
                relationshipText = "Sponsor";
                colorClass = "cat-green";
                break;
            case 3:
                relationshipText = "Employee";
                colorClass = "cat-yellow";
                break;
            case 4:
                relationshipText = "Guardian";
                colorClass = "cat-purple";
                break;
            default:
                relationshipText = `Relationship ${categoryNum}`;
                colorClass = "cat-gray";
        }
    } else {
        // Handle text values (case-insensitive)
        const normalized = categoryStr.replace(/-/g, "").replace(/\s+/g, "");
        switch (normalized) {
            case "student":
                relationshipText = "Student";
                colorClass = "cat-blue";
                break;
            case "guardian":
                relationshipText = "Guardian";
                colorClass = "cat-purple";
                break;
            case "sponsor":
                relationshipText = "Sponsor";
                colorClass = "cat-green";
                break;
            case "employee":
                relationshipText = "Employee";
                colorClass = "cat-yellow";
                break;
            case "parent":
                relationshipText = "Parent";
                colorClass = "cat-purple";
                break;
            case "emergencycontact":
            case "emergency":
                relationshipText = "Emergency Contact";
                colorClass = "cat-orange";
                break;
            case "other":
                relationshipText = "Other";
                colorClass = "cat-gray";
                break;
            default:
    
                // If it's a known text value, capitalize it properly
                if (categoryStr.length > 0) {
                    relationshipText = categoryStr.charAt(0).toUpperCase() + categoryStr.slice(1);
                    colorClass = "cat-blue";
                } else {
                    relationshipText = String(category);
                    colorClass = "cat-gray";
                }
        }
    }

    return (
        <span className={`ui-element cat ${colorClass}`} title={relationshipText}>
            <i className="fas fa-circle mr-1"></i> {relationshipText}
        </span>
    );
};

